import {
  useChatHistoryQuery,
  useAskSqlMutation,
  useGetChatsQuery,
} from "~/shared/api/chat/chat.api";
import { useRef, useEffect, useState } from "react";
import { ChatActions } from "./ chat-actions";
import { MessageBubble } from "./message-bubble";
import { ChatMessage } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";

export const ChatContent = ({
  chatId,
  setActiveChatId,
  onMessageSelect,
  selectedMessage,
}: {
  chatId: string | null;
  setActiveChatId: (chatId: string | null) => void;
  onMessageSelect: (message: ChatMessage) => void;
  selectedMessage: ChatMessage | null;
}) => {
  const t = useT();
  const { data: messages, refetch } = useChatHistoryQuery(chatId as string, {
    skip: !chatId,
  });
  const [askSql, { isLoading }] = useAskSqlMutation();
  const { refetch: refetchChats } = useGetChatsQuery();
  const [input, setInput] = useState("");
  const [pendingUserMsg, setPendingUserMsg] = useState<string | null>(null);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Combine messages with pending user message
  const allMessages: ChatMessage[] = chatId
    ? [
        ...(messages?.history || []),
        ...(pendingUserMsg ? [{ content: pendingUserMsg, role: "user" }] : []),
      ]
    : [];

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [allMessages?.length, showSkeleton]);

  const handleSend = async () => {
    if (!input || isLoading) return;
    setPendingUserMsg(input); // Optimistically show user message
    setShowSkeleton(true); // Show assistant skeleton
    const payload = chatId ? { chat_id: chatId, user_question: input } : { user_question: input };
    setInput("");
    try {
      const response = await askSql(payload).unwrap();
      if (!chatId && response) {
        if (!response.chat_id) return;
        setActiveChatId(response.chat_id); // Set new chat id
        refetchChats();
      }
      refetch();
    } finally {
      setPendingUserMsg(null);
      setShowSkeleton(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col space-y-4">
      <div className="flex-1 overflow-y-auto flex flex-col p-2 bg-brand-blue-light rounded-lg">
        {!chatId ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-base font-medium">
            {t(Namespace.Chat, "writeMessageToStartChat")}
          </div>
        ) : (
          <>
            {allMessages.map((msg, idx) => (
              <MessageBubble
                key={idx}
                content={msg.content}
                isUser={msg.role === "user"}
                isSkeleton={false}
                selected={selectedMessage?.id === msg.id}
                onClick={() => onMessageSelect(msg)}
              />
            ))}
            {showSkeleton && <MessageBubble content="" isUser={false} isSkeleton={true} />}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
      <ChatActions input={input} onInputChange={setInput} onSend={handleSend} loading={isLoading} />
    </div>
  );
};
